import { useMemo } from 'react'
import { NavLink, type NavLinkRenderProps } from 'react-router-dom'

import { type IconName, ICONS } from '@/components/icons/icons'

import { useCarriedSearch } from '../hooks/usePeriod'
import type { PaceStatus } from '../lib/pace'
import styles from './NavItem.module.css'

const DOT: Record<PaceStatus | 'accent', string> = {
  accent: styles.dotAccent ?? '',
  good: styles.dotGood ?? '',
  bad: styles.dotBad ?? '',
  neutral: styles.dotNeutral ?? '',
}

function itemClass({ isActive }: NavLinkRenderProps): string {
  return isActive ? `${styles.item} ${styles.active}` : (styles.item ?? '')
}

function overviewClass({ isActive }: NavLinkRenderProps): string {
  return `${itemClass({ isActive } as NavLinkRenderProps)} ${styles.overview}`
}

interface NavItemProps {
  to: string
  name: string
  status: PaceStatus | 'accent'
  /** Right-hand figure, e.g. `62%` or `n/b`. */
  figure?: string
  figureLabel?: string
  overview?: boolean
  /** Shown instead of the status dot (non-department links). */
  icon?: IconName
  onNavigate?: () => void
}

export function NavItem({ to, name, status, figure, figureLabel, overview, icon, onNavigate }: NavItemProps) {
  const Icon = icon ? ICONS[icon] : null
  // Links keep the selected quarter and budget (`?q=`, `?budget=`).
  const search = useCarriedSearch()
  const target = useMemo(() => ({ pathname: to, search }), [to, search])
  return (
    <NavLink to={target} end className={overview ? overviewClass : itemClass} onClick={onNavigate}>
      {Icon ? (
        <Icon size={14} className={styles.icon} />
      ) : (
        <span className={`${styles.dot} ${DOT[status]}`} aria-hidden="true" />
      )}
      <span className={styles.name}>{name}</span>
      {figure ? (
        <span className={`${styles.pct} num tone-${status === 'accent' ? 'neutral' : status}`}>
          <span aria-hidden="true">{figure}</span>
          {figureLabel ? <span className="visually-hidden">{figureLabel}</span> : null}
        </span>
      ) : null}
    </NavLink>
  )
}
